<template>
  <transition name="fade" mode="out-in">
    <div v-if="loading" class="container py-10 text-center">
      <d-loader />
    </div>
    <div v-else>
      <div class="pb-16">
        <edit-submitted-info v-if="editing" :submission-details.sync="editableSubmissionDetails" :transaction-details.sync="editableTransactionDetails" />
        <view-submitted-info v-else :submission-details.sync="editableSubmissionDetails" :transaction-details.sync="editableTransactionDetails" @save-changes="saveRevisedInformation" />
      </div>

      <div class="fixed bottom-0 z-10 bg-white shadow border-t w-full p-5">
        <div v-if="editableSubmissionDetails.status == RequestStatus.Pending" key="pending" class="container flex justify-between items-center">
          <div class="font-bold text-lg">
            {{ editing ? 'Editing' : 'Viewing' }} Details
          </div>
          <div v-if="!editing">
            <d-btn class="mr-3" color="error" @click="abandonRegistration">
              Abandon
            </d-btn>
            <d-btn class="mr-3" color="success" @click="completeRegistration">
              Approve
            </d-btn>
            <d-btn color="gray-400" @click="manuallyEnteredSubmission">
              Mark as Manually Entered
            </d-btn>
          </div>
          <div v-if="editing" key="editing">
            <d-btn class="mr-3" flat color="error" @click="cancelEditMode">
              Cancel
            </d-btn>
            <d-btn @click="saveRevisedInformation">
              Save Changes
            </d-btn>
          </div>
          <div v-else key="view">
            <d-btn @click="startEditInfo">
              Edit Info
            </d-btn>
          </div>
        </div>

        <div v-else key="notPending" class="container flex justify-between items-center">
          <div class="font-bold text-lg">
            Viewing Details
          </div>

          <div class="font-bold text-lg">
            {{ editableSubmissionDetails.status }}
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
  import { RequestStatus } from '@resources/scripts/models/enums';
  import { getById, update } from '@resources/scripts/services/api/draftingAccreditation.api';
  import axios from 'axios';
  import ConfirmApprovalModal from './components/ConfirmApprovalModal.vue';
  import ViewSubmittedInfo from './components/ViewSubmittedInfo.vue';
  import EditSubmittedInfo from './components/EditSubmittedInfo.vue';

  export default {
    parent: true,
    components: {
      ViewSubmittedInfo,
      EditSubmittedInfo,
    },

    props: {
      submissionId: Number,
    },

    data() {
      return {
        RequestStatus,
        originalSubmissionDetails: {},
        editableSubmissionDetails: {},
        originalTransactionDetails: {},
        editableTransactionDetails: {},
        loading: true,
        editing: false,
      };
    },

    mounted() {
      this.getSubmissionDetails().then(() => {
        this.loading = false;
      });
    },

    methods: {
      async getTransactionDetails() {
        const { data } = await axios.get(`/api/transactions/${this.originalSubmissionDetails.transactionId}`);
        this.originalTransactionDetails = data;
        this.editableTransactionDetails = JSON.parse(JSON.stringify(data));
      },

      async saveTransactionDetails() {
        const { data } = await axios.patch(`/api/transactions/${this.originalSubmissionDetails.transactionId}`, this.editableTransactionDetails);
        this.editableTransactionDetails = data;
        this.originalTransactionDetails = JSON.parse(JSON.stringify(data));
      },

      async getSubmissionDetails() {
        const { data } = await getById(this.submissionId);
        this.originalSubmissionDetails = data;
        this.editableSubmissionDetails = JSON.parse(JSON.stringify(data));
        await this.getTransactionDetails();
      },

      async saveRevisedInformation() {
        const { data } = await update(this.submissionId, this.editableSubmissionDetails);
        this.editableSubmissionDetails = data;
        this.originalSubmissionDetails = JSON.parse(JSON.stringify(data));
        await this.saveTransactionDetails();
        this.editing = false;
      },

      async completeRegistration() {
        this.$modal.open({
          parent: this,
          width: 950,
          component: ConfirmApprovalModal,
          canCancel: ['escape', 'x', 'button'],
          hasModalCard: true,
          props: {
            submissionDetails: this.editableSubmissionDetails,
            submissionId: this.submissionId,
          },
          events: {
            updateRevisedInfo: (data) => {
              this.editableSubmissionDetails = data;
              this.originalSubmissionDetails = data;
            },
          },
        });
      },

      async abandonRegistration() {
        this.$dialog.confirm({
          title: 'Mark as Abandoned',
          message: 'Are you sure you want to mark this submission as <b>abandoned</b>? This action will mark the submission as Abandoned and removed from the listing page. No further action will be taken from this submission.',
          confirmText: 'Continue',
          confirmColor: 'success',
          onConfirm: async () => {
            this.originalSubmissionDetails.status = RequestStatus.Abandoned;
            this.editableSubmissionDetails.status = RequestStatus.Abandoned;
            await this.saveRevisedInformation();
            this.$toast.success('The registration has been abandoned');
          },
        });
      },

      manuallyEnteredSubmission() {
        this.$dialog.confirm({
          title: 'Mark as Manually Entered',
          message: 'Are you sure you want to mark this submission as <b>manually entered</b>? This action will mark the submission as Manually Entered and completed.',
          confirmText: 'Continue',
          confirmColor: 'success',
          onConfirm: async () => {
            this.originalSubmissionDetails.status = RequestStatus.ManuallyEntered;
            this.editableSubmissionDetails.status = RequestStatus.ManuallyEntered;
            await this.saveRevisedInformation();
            this.$toast.success('This submission has been marked as manually entered');
          },
        });
      },

      startEditInfo() {
        this.originalSubmissionDetails = JSON.parse(JSON.stringify(this.editableSubmissionDetails));
        this.editing = true;
      },

      cancelEditMode() {
        this.editing = false;
        this.editableSubmissionDetails = JSON.parse(JSON.stringify(this.originalSubmissionDetails));
      },
    },
  };
</script>